import {
  Dismiss24Regular,
  Settings24Regular,
  Alert24Regular,
  BuildingFactory24Regular,
  Book24Regular,
  Folder24Regular,
  Add20Regular,
  Delete20Regular,
  Edit20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
  Database24Regular,
  BrainCircuit24Regular,
  TagPercent24Regular,
  Money24Regular,
  WalletCreditCard24Regular,
  Wallet24Regular,
  CheckmarkCircle24Regular,
} from "@fluentui/react-icons";
import { CSSProperties, type ComponentType, useCallback, useEffect, useMemo, useState } from "react";
import AiSettingsTab from "./AiSettingsTab";
import FinancialSettingsTab from "./FinancialSettingsTab";
import BankSyncSettingsTab from "./BankSyncSettingsTab";
import CompaniesSettingsTab from "./CompaniesSettingsTab";
import PaymentStatusSettingsTab from "./PaymentStatusSettingsTab";
import IndirectCostSettingsTab from "./IndirectCostSettingsTab";
import CreditControlSettingsTab from "./CreditControlSettingsTab";
import TreasurySettingsTab from "./TreasurySettingsTab";
import themeSystemIcon from "../assets/theme-system-icon.svg";
import {
  createWorkType,
  deleteWorkType,
  getWorkTypes,
  updateWorkType,
  type WorkType,
} from "../clients/clientsApi";
import {
  createBudgetAreaType,
  createBudgetChapterType,
  deleteBudgetAreaType,
  deleteBudgetChapterType,
  getBudgetLineSuggestionSettings,
  getBudgetAreaTypes,
  getBudgetChapterTypes,
  reindexBudgetLines,
  updateBudgetLineSuggestionSettings,
  updateBudgetAreaType,
  updateBudgetChapterType,
  getMarginAlertRanges,
  saveMarginAlertRanges,
  getBudgetPercentageRules,
  saveBudgetPercentageRules,
  getArticleClassificationRules,
  saveArticleClassificationRules,
  getExpenseCategoryRules,
  saveExpenseCategoryRules,
  getAnomalySettings,
  saveAnomalySettings,
  getAiSettings,
  saveAiSettings,
  type AnomalySettings,
  type BudgetAreaType,
  type BudgetChapterType,
  type BudgetPercentageRule,
  type BudgetPercentageRulePayload,
  type ArticleClassificationRule,
  type ArticleClassificationRulePayload,
  type ExpenseCategoryRule,
  type ExpenseCategoryRulePayload,
  type MarginAlertRange,
  type MarginAlertRangePayload,
} from "../orcamentos/orcamentosApi";
import {
  getExpenseCategories,
  type ExpenseCategory,
} from "../obras/obrasApi";
import {
  createExpenseCategory,
  deleteExpenseCategory,
  getExpenseCategoriesTree,
  slugifyCategoryName,
  updateExpenseCategory,
  ApiError as ExpenseCategoryApiError,
  type ExpenseCategoryNode,
} from "../settings/expenseCategoriesApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "./SearchableSelect";

export type ThemeMode = "system" | "light" | "dark";

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  themeMode: ThemeMode;
  onThemeModeChange: (mode: ThemeMode) => void;
}

type SettingsTab =
  | "general"
  | "empresas"
  | "notifications"
  | "dados"
  | "tipos-obras"
  | "margin-alerts"
  | "areas-orcamento"
  | "capitulos-orcamento"
  | "linhas-percentuais"
  | "classificacao"
  | "plano-categorias"
  | "categorias-despesa"
  | "anomalias"
  | "financeiro"
  | "bancos"
  | "estados-pagamento"
  | "custos-indiretos"
  | "credit-control"
  | "treasury"
  | "ia";


// Navegação das Definições por secções — um único catálogo alimenta a sidebar
// desktop (com cabeçalhos) e a barra inferior mobile (labels curtas).
interface SettingsNavItem {
  tab: SettingsTab;
  label: string;
  shortLabel: string;
  icon: ComponentType<{ className?: string }>;
}

const SETTINGS_NAV_SECTIONS: {
  title: string | null;
  items: SettingsNavItem[];
}[] = [
  {
    title: null,
    items: [
      { tab: "general", label: "Geral", shortLabel: "Geral", icon: Settings24Regular },
      { tab: "dados", label: "Dados", shortLabel: "Dados", icon: Database24Regular },
      { tab: "empresas", label: "Empresas", shortLabel: "Empr.", icon: BuildingFactory24Regular },
      { tab: "notifications", label: "Notificações", shortLabel: "Notif.", icon: Alert24Regular },
    ],
  },
  {
    title: "Obras e Orçamentos",
    items: [
      { tab: "areas-orcamento", label: "Áreas de Orçamento", shortLabel: "Áreas", icon: Folder24Regular },
      { tab: "capitulos-orcamento", label: "Capítulos de Orçamento", shortLabel: "Capít.", icon: Book24Regular },
      { tab: "linhas-percentuais", label: "Linhas Percentuais", shortLabel: "%", icon: TagPercent24Regular },
      { tab: "margin-alerts", label: "Semáforo de Margens", shortLabel: "Marg.", icon: Alert24Regular },
      { tab: "tipos-obras", label: "Tipos de Obras", shortLabel: "Tipos", icon: BuildingFactory24Regular },
    ],
  },
  {
    title: "Faturas e Despesas",
    items: [
      { tab: "anomalias", label: "Anomalias", shortLabel: "Anom.", icon: Alert24Regular },
      { tab: "classificacao", label: "Classificação", shortLabel: "Classif.", icon: BuildingFactory24Regular },
      { tab: "estados-pagamento", label: "Estados de Pagamento", shortLabel: "Estados", icon: CheckmarkCircle24Regular },
      { tab: "plano-categorias", label: "Plano de Categorias", shortLabel: "Plano", icon: Folder24Regular },
      { tab: "categorias-despesa", label: "Regras de Categorias", shortLabel: "Regras", icon: Money24Regular },
    ],
  },
  {
    title: "Financeiro",
    items: [
      { tab: "credit-control", label: "Controlo de Crédito", shortLabel: "Crédito", icon: WalletCreditCard24Regular },
      { tab: "custos-indiretos", label: "Custos Indiretos", shortLabel: "Indir.", icon: TagPercent24Regular },
      { tab: "financeiro", label: "Parâmetros Financeiros", shortLabel: "Financ.", icon: Money24Regular },
      { tab: "treasury", label: "Tesouraria", shortLabel: "Tesouraria", icon: Wallet24Regular },
      { tab: "bancos", label: "Bancos", shortLabel: "Bancos", icon: WalletCreditCard24Regular },
    ],
  },
  {
    title: null,
    items: [
      { tab: "ia", label: "Inteligência Artificial", shortLabel: "IA", icon: BrainCircuit24Regular },
    ],
  },
];

type WorkTypeRow = WorkType & { isNew?: boolean };
type BudgetAreaTypeRow = BudgetAreaType & { isNew?: boolean };
type BudgetChapterTypeRow = BudgetChapterType & { isNew?: boolean };

const buildEditingKey = (scope: string, id: number) => `${scope}:${id}`;

const createTempId = () =>
  -Math.floor(Date.now() + Math.random() * 1000);

/**
 * Classificação de custo de uma categoria de despesa. É ela que decide se a despesa pode ser
 * imputada a uma obra: numa categoria de estrutura (combustíveis, estaleiro) a leitura automática
 * de documentos nunca atribui obra. Sem este campo exposto, a marca só existia nas categorias que
 * vieram semeadas e não havia como classificar as que o utilizador cria.
 */
const COST_TYPE_OPTIONS = [
  { value: "", label: "Por classificar" },
  { value: "work", label: "De obra" },
  { value: "structural", label: "De estrutura" },
] as const;

function costTypeToFlag(value: string): boolean | null {
  if (value === "structural") return true;
  if (value === "work") return false;
  return null;
}

function flagToCostType(flag: boolean | null | undefined): string {
  if (flag === true) return "structural";
  if (flag === false) return "work";
  return "";
}

function CostTypeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="border border-border rounded px-2 py-1 bg-background text-foreground"
      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "9rem" }}
    >
      {COST_TYPE_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function costTypeBadge(flag: boolean | null | undefined) {
  const isStructure = flag === true;
  return (
    <span
      className="px-2 py-0.5 flex-shrink-0"
      style={{
        fontSize: "var(--text-caption)",
        borderRadius: "var(--radius-button)",
        backgroundColor: flag === null || flag === undefined ? "var(--muted)" : isStructure ? "rgba(34,197,94,0.14)" : "var(--accent)",
        color: flag === null || flag === undefined ? "var(--secondary-text)" : isStructure ? "#15803d" : "var(--primary)",
      }}
    >
      {flag === null || flag === undefined ? "Por classificar" : isStructure ? "Estrutura (sem obra)" : "De obra"}
    </span>
  );
}

export function SettingsDialog({
  isOpen,
  onClose,
  themeMode,
  onThemeModeChange,
}: SettingsDialogProps) {
  const [activeTab, setActiveTab] =
    useState<SettingsTab>("general");
  const [isAnimating, setIsAnimating] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(
    null,
  );
  const [editingValues, setEditingValues] = useState<
    Record<string, string>
  >({});
  const [workTypes, setWorkTypes] = useState<WorkTypeRow[]>(
    [],
  );
  const [areasOrcamento, setAreasOrcamento] = useState<
    BudgetAreaTypeRow[]
  >([]);
  const [capitulosOrcamento, setCapitulosOrcamento] =
    useState<BudgetChapterTypeRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [workTypesError, setWorkTypesError] = useState<
    string | null
  >(null);
  const [areasError, setAreasError] = useState<string | null>(
    null,
  );
  const [capitulosError, setCapitulosError] = useState<
    string | null
  >(null);
  const [reindexLoading, setReindexLoading] =
    useState(false);
  const [reindexError, setReindexError] = useState<
    string | null
  >(null);
  const [reindexSuccess, setReindexSuccess] = useState<
    string | null
  >(null);
  const [suggestionLimitValue, setSuggestionLimitValue] =
    useState("");
  const [suggestionLimitLoading, setSuggestionLimitLoading] =
    useState(false);
  const [suggestionLimitSaving, setSuggestionLimitSaving] =
    useState(false);
  const [suggestionLimitError, setSuggestionLimitError] =
    useState<string | null>(null);
  const [suggestionLimitSuccess, setSuggestionLimitSuccess] =
    useState<string | null>(null);

  // Margin alert ranges
  const [marginRanges, setMarginRanges] = useState<MarginAlertRange[]>([]);
  const [marginRangesLoading, setMarginRangesLoading] = useState(false);
  const [marginRangesSaving, setMarginRangesSaving] = useState(false);
  const [marginRangesError, setMarginRangesError] = useState<string | null>(null);
  const [marginRangesSuccess, setMarginRangesSuccess] = useState<string | null>(null);
  const [marginRangesDraft, setMarginRangesDraft] = useState<MarginAlertRangePayload[]>([]);

  // Budget percentage rules
  const [, setPercentageRules] = useState<BudgetPercentageRule[]>([]);
  const [percentageRulesLoading, setPercentageRulesLoading] = useState(false);
  const [percentageRulesSaving, setPercentageRulesSaving] = useState(false);
  const [percentageRulesError, setPercentageRulesError] = useState<string | null>(null);
  const [percentageRulesSuccess, setPercentageRulesSuccess] = useState<string | null>(null);
  const [percentageRulesDraft, setPercentageRulesDraft] = useState<BudgetPercentageRulePayload[]>([]);

  // Article classification rules
  const [, setClassificationRules] = useState<ArticleClassificationRule[]>([]);
  const [classificationRulesLoading, setClassificationRulesLoading] = useState(false);
  const [classificationRulesSaving, setClassificationRulesSaving] = useState(false);
  const [classificationRulesError, setClassificationRulesError] = useState<string | null>(null);
  const [classificationRulesSuccess, setClassificationRulesSuccess] = useState<string | null>(null);
  const [classificationRulesDraft, setClassificationRulesDraft] = useState<ArticleClassificationRulePayload[]>([]);

  // Expense category rules (Lote A-18)
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [, setExpenseCategoryRules] = useState<ExpenseCategoryRule[]>([]);
  const [expenseRulesLoading, setExpenseRulesLoading] = useState(false);
  const [expenseRulesSaving, setExpenseRulesSaving] = useState(false);
  const [expenseRulesError, setExpenseRulesError] = useState<string | null>(null);
  const [expenseRulesSuccess, setExpenseRulesSuccess] = useState<string | null>(null);
  const [expenseRulesDraft, setExpenseRulesDraft] = useState<ExpenseCategoryRulePayload[]>([]);

  // Expense category tree (Lote A-23 [2.4.1]) — 2-level plano de categorias.
  const [categoryTree, setCategoryTree] = useState<ExpenseCategoryNode[]>([]);
  const [categoryTreeLoading, setCategoryTreeLoading] = useState(false);
  const [categoryTreeError, setCategoryTreeError] = useState<string | null>(null);
  const [categoryBusy, setCategoryBusy] = useState(false);
  // New root-category form.
  const [newRootName, setNewRootName] = useState("");
  const [newRootCode, setNewRootCode] = useState("");
  const [newRootCodeTouched, setNewRootCodeTouched] = useState(false);
  const [newRootCostType, setNewRootCostType] = useState("");
  // Inline subcategory creation, keyed by parent root id.
  const [subParentId, setSubParentId] = useState<number | null>(null);
  const [newSubName, setNewSubName] = useState("");
  const [newSubCode, setNewSubCode] = useState("");
  const [newSubCodeTouched, setNewSubCodeTouched] = useState(false);
  const [newSubCostType, setNewSubCostType] = useState("");
  // Inline edit of an existing node, keyed by node id.
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryCode, setEditCategoryCode] = useState("");
  const [editCategoryCostType, setEditCategoryCostType] = useState("");

  // Anomaly detection settings (Lote A-22 [2.1.9])
  const [anomalyDraft, setAnomalyDraft] = useState<AnomalySettings | null>(null);
  const [anomalyMultiplierInput, setAnomalyMultiplierInput] = useState("");
  const [anomalyMinHistoryInput, setAnomalyMinHistoryInput] = useState("");
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalySaving, setAnomalySaving] = useState(false);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);
  const [anomalySuccess, setAnomalySuccess] = useState<string | null>(null);

  // Default margin applied when a price suggestion has no margin (lives in AiSettings).
  const [defaultMarginInput, setDefaultMarginInput] = useState<number>(120);
  const [defaultMarginSaving, setDefaultMarginSaving] = useState(false);

  const sortedWorkTypes = useMemo(() => {
    return [...workTypes].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
  }, [workTypes]);

  const sortedAreas = useMemo(() => {
    return [...areasOrcamento].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
  }, [areasOrcamento]);

  const sortedCapitulos = useMemo(() => {
    return [...capitulosOrcamento].sort((a, b) => {
      if (a.budgetAreaTypeId !== b.budgetAreaTypeId) {
        return a.budgetAreaTypeId - b.budgetAreaTypeId;
      }
      return a.sortOrder - b.sortOrder;
    });
  }, [capitulosOrcamento]);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setEditingKey(null);
    setEditingValues({});
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setIsLoading(true);
    setLoadError(null);

    Promise.all([
      getWorkTypes(),
      getBudgetAreaTypes(),
      getBudgetChapterTypes(),
    ])
      .then(([workTypesData, areasData, capitulosData]) => {
        if (!isMounted) return;
        setWorkTypes(
          workTypesData.map((item) => ({
            ...item,
          })),
        );
        setAreasOrcamento(
          areasData.map((item) => ({
            ...item,
          })),
        );
        setCapitulosOrcamento(
          capitulosData.map((item) => ({
            ...item,
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setLoadError(
          "Não foi possível carregar as tabelas de referência.",
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isMounted = true;
    setSuggestionLimitLoading(true);
    setSuggestionLimitError(null);
    setSuggestionLimitSuccess(null);

    getBudgetLineSuggestionSettings()
      .then((settings) => {
        if (!isMounted) {
          return;
        }
        setSuggestionLimitValue(
          settings.limit !== null && settings.limit !== undefined
            ? settings.limit.toString()
            : "",
        );
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setSuggestionLimitError(
          "Não foi possível carregar o limite das sugestões.",
        );
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setSuggestionLimitLoading(false);
      });

    // Load margin alert ranges
    setMarginRangesLoading(true);
    getMarginAlertRanges()
      .then((ranges) => {
        if (!isMounted) return;
        setMarginRanges(ranges);
        setMarginRangesDraft(
          ranges.map((r) => ({
            minPercent: r.minPercent,
            maxPercent: r.maxPercent,
            color: r.color,
            label: r.label,
            position: r.position,
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setMarginRangesError("Não foi possível carregar os alertas de margem.");
      })
      .finally(() => {
        if (!isMounted) return;
        setMarginRangesLoading(false);
      });

    // Load budget percentage rules
    setPercentageRulesLoading(true);
    getBudgetPercentageRules()
      .then((rules) => {
        if (!isMounted) return;
        setPercentageRules(rules);
        setPercentageRulesDraft(
          rules.map((rule) => ({
            description: rule.description,
            defaultPercentage: rule.defaultPercentage,
            position: rule.position,
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setPercentageRulesError("Não foi possível carregar as linhas percentuais.");
      })
      .finally(() => {
        if (!isMounted) return;
        setPercentageRulesLoading(false);
      });

    // Load article classification rules
    setClassificationRulesLoading(true);
    getArticleClassificationRules()
      .then((rules) => {
        if (!isMounted) return;
        setClassificationRules(rules);
        setClassificationRulesDraft(
          rules.map((rule) => ({
            matchType: rule.matchType,
            matchValue: rule.matchValue,
            classificationCode: rule.classificationCode,
            position: rule.position,
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setClassificationRulesError("Não foi possível carregar as regras de classificação.");
      })
      .finally(() => {
        if (!isMounted) return;
        setClassificationRulesLoading(false);
      });

    // Load expense category rules + reference categories (Lote A-18)
    setExpenseRulesLoading(true);
    Promise.all([getExpenseCategories(), getExpenseCategoryRules()])
      .then(([categories, rules]) => {
        if (!isMounted) return;
        setExpenseCategories(categories);
        setExpenseCategoryRules(rules);
        setExpenseRulesDraft(
          rules.map((rule) => ({
            matchType: rule.matchType,
            matchValue: rule.matchValue,
            categoryCode: rule.categoryCode,
            position: rule.position,
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setExpenseRulesError("Não foi possível carregar as regras de categorias de despesa.");
      })
      .finally(() => {
        if (!isMounted) return;
        setExpenseRulesLoading(false);
      });

    // Load the 2-level expense-category tree (Lote A-23 [2.4.1]).
    loadCategoryTree();

    // Load anomaly detection settings (Lote A-22 [2.1.9])
    setAnomalyLoading(true);
    setAnomalyError(null);
    setAnomalySuccess(null);
    getAnomalySettings()
      .then((settings) => {
        if (!isMounted) return;
        setAnomalyDraft(settings);
        setAnomalyMultiplierInput(
          Number.isFinite(settings.valueAboveUsualMultiplier)
            ? settings.valueAboveUsualMultiplier.toString()
            : "",
        );
        setAnomalyMinHistoryInput(
          Number.isFinite(settings.valueAboveUsualMinHistory)
            ? settings.valueAboveUsualMinHistory.toString()
            : "",
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setAnomalyError("Não foi possível carregar a configuração de anomalias.");
      })
      .finally(() => {
        if (!isMounted) return;
        setAnomalyLoading(false);
      });

    // Load default suggestion margin from AI settings
    getAiSettings()
      .then((s) => {
        if (!isMounted) return;
        if (typeof s.defaultSuggestionMarginPercent === 'number' && s.defaultSuggestionMarginPercent > 0) {
          setDefaultMarginInput(s.defaultSuggestionMarginPercent);
        }
      })
      .catch(() => {
        // ignore — fallback default 120 already set
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const handleReindexBudgetLines = async () => {
    setReindexLoading(true);
    setReindexError(null);
    setReindexSuccess(null);

    try {
      const result = await reindexBudgetLines();
      setReindexSuccess(
        `Reindexação iniciada para ${result.queuedBudgets} orçamentos.`,
      );
    } catch {
      setReindexError(
        "Não foi possível iniciar a reindexação do Azure Search.",
      );
    } finally {
      setReindexLoading(false);
    }
  };

  const handleSaveSuggestionLimit = async () => {
    setSuggestionLimitSaving(true);
    setSuggestionLimitError(null);
    setSuggestionLimitSuccess(null);

    const trimmed = suggestionLimitValue.trim();
    const parsedLimit =
      trimmed === "" ? null : Number.parseInt(trimmed, 10);

    if (
      parsedLimit !== null &&
      (!Number.isFinite(parsedLimit) || parsedLimit <= 0)
    ) {
      setSuggestionLimitError("Indique um número válido (>= 1).");
      setSuggestionLimitSaving(false);
      return;
    }

    try {
      const result = await updateBudgetLineSuggestionSettings({
        limit: parsedLimit,
      });
      setSuggestionLimitValue(
        result.limit !== null && result.limit !== undefined
          ? result.limit.toString()
          : "",
      );
      setSuggestionLimitSuccess("Limite atualizado com sucesso.");
    } catch {
      setSuggestionLimitError(
        "Não foi possível atualizar o limite das sugestões.",
      );
    } finally {
      setSuggestionLimitSaving(false);
    }
  };

  const handleSaveMarginRanges = async () => {
    setMarginRangesSaving(true);
    setMarginRangesError(null);
    setMarginRangesSuccess(null);
    try {
      const saved = await saveMarginAlertRanges(marginRangesDraft);
      setMarginRanges(saved);
      setMarginRangesDraft(
        saved.map((r) => ({
          minPercent: r.minPercent,
          maxPercent: r.maxPercent,
          color: r.color,
          label: r.label,
          position: r.position,
        })),
      );
      setMarginRangesSuccess("Alertas de margem guardados.");
    } catch {
      setMarginRangesError("Não foi possível guardar os alertas de margem.");
    } finally {
      setMarginRangesSaving(false);
    }
  };

  const handleAddMarginRange = () => {
    const nextPos = marginRangesDraft.length;
    setMarginRangesDraft((prev) => [
      ...prev,
      { minPercent: 0, maxPercent: null, color: "#6b7280", label: null, position: nextPos },
    ]);
  };

  const handleRemoveMarginRange = (index: number) => {
    setMarginRangesDraft((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((r, i) => ({ ...r, position: i })),
    );
  };

  const handleMarginRangeChange = (
    index: number,
    field: keyof MarginAlertRangePayload,
    value: string | number | null,
  ) => {
    setMarginRangesDraft((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, [field]: value } : r,
      ),
    );
  };

  const handleSavePercentageRules = async () => {
    setPercentageRulesSaving(true);
    setPercentageRulesError(null);
    setPercentageRulesSuccess(null);
    try {
      const trimmed = percentageRulesDraft.map((rule, index) => ({
        description: rule.description.trim(),
        defaultPercentage: rule.defaultPercentage,
        position: index,
      }));

      const hasEmpty = trimmed.some((rule) => rule.description === "");
      if (hasEmpty) {
        setPercentageRulesError("Todas as regras devem ter uma descrição.");
        setPercentageRulesSaving(false);
        return;
      }

      const outOfRange = trimmed.some(
        (rule) => rule.defaultPercentage < 0 || rule.defaultPercentage > 100,
      );
      if (outOfRange) {
        setPercentageRulesError("A percentagem tem de estar entre 0 e 100.");
        setPercentageRulesSaving(false);
        return;
      }

      const saved = await saveBudgetPercentageRules(trimmed);
      setPercentageRules(saved);
      setPercentageRulesDraft(
        saved.map((rule) => ({
          description: rule.description,
          defaultPercentage: rule.defaultPercentage,
          position: rule.position,
        })),
      );
      setPercentageRulesSuccess("Linhas percentuais guardadas.");
    } catch {
      setPercentageRulesError("Não foi possível guardar as linhas percentuais.");
    } finally {
      setPercentageRulesSaving(false);
    }
  };

  const handleAddPercentageRule = () => {
    const nextPos = percentageRulesDraft.length;
    setPercentageRulesDraft((prev) => [
      ...prev,
      { description: "", defaultPercentage: 7, position: nextPos },
    ]);
  };

  const handleRemovePercentageRule = (index: number) => {
    setPercentageRulesDraft((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((rule, i) => ({ ...rule, position: i })),
    );
  };

  const handlePercentageRuleChange = (
    index: number,
    field: keyof BudgetPercentageRulePayload,
    value: string | number,
  ) => {
    setPercentageRulesDraft((prev) =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    );
  };

  const handleSaveClassificationRules = async () => {
    setClassificationRulesSaving(true);
    setClassificationRulesError(null);
    setClassificationRulesSuccess(null);
    try {
      const trimmed = classificationRulesDraft.map((rule, index) => ({
        matchType: rule.matchType,
        matchValue: rule.matchValue.trim(),
        classificationCode: rule.classificationCode,
        position: index,
      }));

      const hasEmpty = trimmed.some((rule) => rule.matchValue === "");
      if (hasEmpty) {
        setClassificationRulesError("Todas as regras devem ter um valor a procurar.");
        setClassificationRulesSaving(false);
        return;
      }

      const saved = await saveArticleClassificationRules(trimmed);
      setClassificationRules(saved);
      setClassificationRulesDraft(
        saved.map((rule) => ({
          matchType: rule.matchType,
          matchValue: rule.matchValue,
          classificationCode: rule.classificationCode,
          position: rule.position,
        })),
      );
      setClassificationRulesSuccess("Regras de classificação guardadas.");
    } catch {
      setClassificationRulesError("Não foi possível guardar as regras de classificação.");
    } finally {
      setClassificationRulesSaving(false);
    }
  };

  const handleAddClassificationRule = () => {
    const nextPos = classificationRulesDraft.length;
    setClassificationRulesDraft((prev) => [
      ...prev,
      { matchType: "keyword", matchValue: "", classificationCode: "trabalho", position: nextPos },
    ]);
  };

  const handleRemoveClassificationRule = (index: number) => {
    setClassificationRulesDraft((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((rule, i) => ({ ...rule, position: i })),
    );
  };

  const handleClassificationRuleChange = (
    index: number,
    field: keyof ArticleClassificationRulePayload,
    value: string | number,
  ) => {
    setClassificationRulesDraft((prev) =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    );
  };

  const handleSaveExpenseRules = async () => {
    setExpenseRulesSaving(true);
    setExpenseRulesError(null);
    setExpenseRulesSuccess(null);
    try {
      const trimmed = expenseRulesDraft.map((rule, index) => ({
        matchType: rule.matchType,
        matchValue: rule.matchValue.trim(),
        categoryCode: rule.categoryCode,
        position: index,
      }));

      const hasEmpty = trimmed.some((rule) => rule.matchValue === "");
      if (hasEmpty) {
        setExpenseRulesError("Todas as regras devem ter um valor a procurar.");
        setExpenseRulesSaving(false);
        return;
      }

      const saved = await saveExpenseCategoryRules(trimmed);
      setExpenseCategoryRules(saved);
      setExpenseRulesDraft(
        saved.map((rule) => ({
          matchType: rule.matchType,
          matchValue: rule.matchValue,
          categoryCode: rule.categoryCode,
          position: rule.position,
        })),
      );
      setExpenseRulesSuccess("Regras de categorias de despesa guardadas.");
    } catch {
      setExpenseRulesError("Não foi possível guardar as regras de categorias de despesa.");
    } finally {
      setExpenseRulesSaving(false);
    }
  };

  const handleAddExpenseRule = () => {
    const nextPos = expenseRulesDraft.length;
    const defaultCode = categorySelectOptions[0]?.value ?? expenseCategories[0]?.code ?? "outros";
    setExpenseRulesDraft((prev) => [
      ...prev,
      { matchType: "keyword", matchValue: "", categoryCode: defaultCode, position: nextPos },
    ]);
  };

  const handleRemoveExpenseRule = (index: number) => {
    setExpenseRulesDraft((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((rule, i) => ({ ...rule, position: i })),
    );
  };

  const handleExpenseRuleChange = (
    index: number,
    field: keyof ExpenseCategoryRulePayload,
    value: string | number,
  ) => {
    setExpenseRulesDraft((prev) =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    );
  };

  // [2.4.1] Load the 2-level expense-category tree. Reused by the "Plano de Categorias" tab
  // and by the rules tab's category <select> (so new categories appear immediately).
  const loadCategoryTree = useCallback(async () => {
    setCategoryTreeLoading(true);
    setCategoryTreeError(null);
    try {
      setCategoryTree(await getExpenseCategoriesTree());
    } catch {
      setCategoryTreeError("Não foi possível carregar o plano de categorias.");
    } finally {
      setCategoryTreeLoading(false);
    }
  }, []);

  // Roots sorted, each with its sorted children — drives both the tree UI and the indented select.
  const sortedCategoryRoots = useMemo(() => {
    const roots = categoryTree
      .filter((node) => node.parentId === null)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
    return roots.map((root) => ({
      root,
      children: categoryTree
        .filter((node) => node.parentId === root.id)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
    }));
  }, [categoryTree]);

  // Flat, ordered options (root then its children prefixed with "— ") for the rules <select>.
  const categorySelectOptions = useMemo(() => {
    const options: SearchableSelectOption<string>[] = [];
    sortedCategoryRoots.forEach(({ root, children }) => {
      options.push({ value: root.code, label: root.name });
      // O nome da raiz vai em `keywords`: procurar pela categoria-mãe traz também
      // as suas sub-categorias, que só mostram o travessão que as indenta.
      children.forEach((child) =>
        options.push({
          value: child.code,
          label: `— ${child.name}`,
          keywords: root.name,
        }),
      );
    });
    return options;
  }, [sortedCategoryRoots]);

  const handleCreateRootCategory = async () => {
    const name = newRootName.trim();
    const code = (newRootCodeTouched ? newRootCode : slugifyCategoryName(newRootName)).trim();
    if (!name) {
      setCategoryTreeError("O nome da categoria é obrigatório.");
      return;
    }
    if (!code) {
      setCategoryTreeError("O código da categoria é obrigatório.");
      return;
    }
    setCategoryBusy(true);
    setCategoryTreeError(null);
    try {
      await createExpenseCategory({
        code,
        name,
        parentId: null,
        isStructural: costTypeToFlag(newRootCostType),
      });
      setNewRootName("");
      setNewRootCode("");
      setNewRootCodeTouched(false);
      setNewRootCostType("");
      await loadCategoryTree();
    } catch (err) {
      setCategoryTreeError(
        err instanceof ExpenseCategoryApiError
          ? err.message
          : "Não foi possível criar a categoria.",
      );
    } finally {
      setCategoryBusy(false);
    }
  };

  const startAddSubcategory = (parentId: number) => {
    setSubParentId(parentId);
    setNewSubName("");
    setNewSubCode("");
    setNewSubCodeTouched(false);
    // Herda a classificação da categoria-pai: uma subcategoria de Estaleiro é de estrutura como ela.
    setNewSubCostType(
      flagToCostType(categoryTree.find((node) => node.id === parentId)?.isStructural),
    );
    setCategoryTreeError(null);
  };

  const handleCreateSubcategory = async (parentId: number) => {
    const name = newSubName.trim();
    const code = (newSubCodeTouched ? newSubCode : slugifyCategoryName(newSubName)).trim();
    if (!name) {
      setCategoryTreeError("O nome da subcategoria é obrigatório.");
      return;
    }
    if (!code) {
      setCategoryTreeError("O código da subcategoria é obrigatório.");
      return;
    }
    setCategoryBusy(true);
    setCategoryTreeError(null);
    try {
      await createExpenseCategory({
        code,
        name,
        parentId,
        isStructural: costTypeToFlag(newSubCostType),
      });
      setSubParentId(null);
      setNewSubName("");
      setNewSubCode("");
      setNewSubCodeTouched(false);
      setNewSubCostType("");
      await loadCategoryTree();
    } catch (err) {
      setCategoryTreeError(
        err instanceof ExpenseCategoryApiError
          ? err.message
          : "Não foi possível criar a subcategoria.",
      );
    } finally {
      setCategoryBusy(false);
    }
  };

  const startEditCategory = (node: ExpenseCategoryNode) => {
    setEditingCategoryId(node.id);
    setEditCategoryName(node.name);
    setEditCategoryCode(node.code);
    setEditCategoryCostType(flagToCostType(node.isStructural));
    setCategoryTreeError(null);
  };

  const handleSaveCategoryEdit = async (node: ExpenseCategoryNode) => {
    const name = editCategoryName.trim();
    const code = editCategoryCode.trim();
    if (!name || !code) {
      setCategoryTreeError("Nome e código são obrigatórios.");
      return;
    }
    setCategoryBusy(true);
    setCategoryTreeError(null);
    try {
      // O pedido é autoritário: mandar sempre a classificação, senão guardar um nome apagava-a.
      await updateExpenseCategory(node.id, {
        code,
        name,
        parentId: node.parentId,
        isStructural: costTypeToFlag(editCategoryCostType),
      });
      setEditingCategoryId(null);
      await loadCategoryTree();
    } catch (err) {
      setCategoryTreeError(
        err instanceof ExpenseCategoryApiError
          ? err.message
          : "Não foi possível atualizar a categoria.",
      );
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleDeleteCategory = async (node: ExpenseCategoryNode) => {
    setCategoryBusy(true);
    setCategoryTreeError(null);
    try {
      await deleteExpenseCategory(node.id);
      await loadCategoryTree();
    } catch (err) {
      // 400 = tem filhos / em uso → mostra a mensagem do servidor (não bloqueia com window.confirm).
      setCategoryTreeError(
        err instanceof ExpenseCategoryApiError
          ? err.message
          : "Não foi possível eliminar a categoria.",
      );
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleAnomalyToggle = (
    field:
      | "nifCheckEnabled"
      | "newSupplierEnabled"
      | "valueAboveUsualEnabled"
      | "weekendDateEnabled"
      | "atypicalCategoryEnabled",
    value: boolean,
  ) => {
    setAnomalyDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveAnomalySettings = async () => {
    if (!anomalyDraft) return;
    setAnomalyError(null);
    setAnomalySuccess(null);

    // Guard numeric inputs: clearing a field must NOT silently zero/block the save (past bug).
    const multiplierTrimmed = anomalyMultiplierInput.trim();
    const parsedMultiplier = multiplierTrimmed === "" ? NaN : Number(multiplierTrimmed);
    if (!Number.isFinite(parsedMultiplier) || parsedMultiplier <= 0) {
      setAnomalyError("O multiplicador de valor acima do habitual tem de ser um número maior que 0.");
      return;
    }

    const minHistoryTrimmed = anomalyMinHistoryInput.trim();
    const parsedMinHistory =
      minHistoryTrimmed === "" ? NaN : Number.parseInt(minHistoryTrimmed, 10);
    if (!Number.isFinite(parsedMinHistory) || parsedMinHistory < 1) {
      setAnomalyError("O histórico mínimo tem de ser um número inteiro >= 1.");
      return;
    }

    setAnomalySaving(true);
    try {
      const saved = await saveAnomalySettings({
        ...anomalyDraft,
        valueAboveUsualMultiplier: parsedMultiplier,
        valueAboveUsualMinHistory: parsedMinHistory,
      });
      setAnomalyDraft(saved);
      setAnomalyMultiplierInput(
        Number.isFinite(saved.valueAboveUsualMultiplier)
          ? saved.valueAboveUsualMultiplier.toString()
          : "",
      );
      setAnomalyMinHistoryInput(
        Number.isFinite(saved.valueAboveUsualMinHistory)
          ? saved.valueAboveUsualMinHistory.toString()
          : "",
      );
      setAnomalySuccess("Configuração de anomalias guardada.");
      setTimeout(() => setAnomalySuccess(null), 3000);
    } catch {
      setAnomalyError("Não foi possível guardar a configuração de anomalias.");
    } finally {
      setAnomalySaving(false);
    }
  };

  const handleEdit = (scope: string, id: number, value: string) => {
    const key = buildEditingKey(scope, id);
    setEditingKey(key);
    setEditingValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearEditing = (key: string) => {
    setEditingKey(null);
    setEditingValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddTipoObra = () => {
    const nextSortOrder =
      workTypes.reduce(
        (max, item) => Math.max(max, item.sortOrder),
        0,
      ) + 1;
    const newTipo: WorkTypeRow = {
      id: createTempId(),
      name: "",
      sortOrder: nextSortOrder,
      isNew: true,
    };
    setWorkTypes([...workTypes, newTipo]);
    handleEdit("work-type", newTipo.id, "");
  };

  const handleSaveTipoObra = async (tipo: WorkTypeRow) => {
    const key = buildEditingKey("work-type", tipo.id);
    const nome = (editingValues[key] ?? tipo.name).trim();

    if (!nome) {
      setWorkTypesError(
        "O nome do tipo de obra é obrigatório.",
      );
      if (tipo.isNew) {
        setWorkTypes(
          workTypes.filter((item) => item.id !== tipo.id),
        );
      }
      clearEditing(key);
      return;
    }

    setWorkTypesError(null);

    try {
      if (tipo.isNew) {
        const created = await createWorkType({
          name: nome,
          sortOrder: tipo.sortOrder,
        });
        setWorkTypes((prev) =>
          prev.map((item) =>
            item.id === tipo.id ? created : item,
          ),
        );
      } else {
        const updated = await updateWorkType(tipo.id, {
          name: nome,
          sortOrder: tipo.sortOrder,
        });
        setWorkTypes((prev) =>
          prev.map((item) =>
            item.id === tipo.id ? updated : item,
          ),
        );
      }
      clearEditing(key);
    } catch {
      setWorkTypesError(
        "Não foi possível guardar o tipo de obra.",
      );
    }
  };

  const handleCancelTipoObra = (tipo: WorkTypeRow) => {
    const key = buildEditingKey("work-type", tipo.id);
    if (tipo.isNew) {
      setWorkTypes(
        workTypes.filter((item) => item.id !== tipo.id),
      );
    }
    clearEditing(key);
  };

  const handleDeleteTipoObra = async (id: number) => {
    const item = workTypes.find((tipo) => tipo.id === id);
    if (!item) return;

    if (item.isNew) {
      setWorkTypes(workTypes.filter((tipo) => tipo.id !== id));
      return;
    }

    try {
      await deleteWorkType(id);
      setWorkTypes(workTypes.filter((tipo) => tipo.id !== id));
    } catch {
      setWorkTypesError(
        "Não foi possível eliminar o tipo de obra.",
      );
    }
  };

  const handleMoveTipoObra = async (
    id: number,
    direction: -1 | 1,
  ) => {
    const ordered = [...sortedWorkTypes];
    const currentIndex = ordered.findIndex(
      (item) => item.id === id,
    );
    const swapIndex = currentIndex + direction;

    if (currentIndex < 0 || swapIndex < 0) return;
    if (swapIndex >= ordered.length) return;

    const current = ordered[currentIndex];
    const swap = ordered[swapIndex];

    const updatedCurrent = {
      ...current,
      sortOrder: swap.sortOrder,
    };
    const updatedSwap = {
      ...swap,
      sortOrder: current.sortOrder,
    };

    setWorkTypes((prev) =>
      prev.map((item) => {
        if (item.id === updatedCurrent.id) return updatedCurrent;
        if (item.id === updatedSwap.id) return updatedSwap;
        return item;
      }),
    );

    try {
      const updates = [];
      if (!updatedCurrent.isNew) {
        updates.push(
          updateWorkType(updatedCurrent.id, {
            name: updatedCurrent.name,
            sortOrder: updatedCurrent.sortOrder,
          }),
        );
      }
      if (!updatedSwap.isNew) {
        updates.push(
          updateWorkType(updatedSwap.id, {
            name: updatedSwap.name,
            sortOrder: updatedSwap.sortOrder,
          }),
        );
      }
      await Promise.all(updates);
    } catch {
      setWorkTypesError(
        "Não foi possível atualizar a ordem dos tipos de obra.",
      );
    }
  };

  const handleAddArea = () => {
    const nextSortOrder =
      areasOrcamento.reduce(
        (max, item) => Math.max(max, item.sortOrder),
        0,
      ) + 1;
    const newArea: BudgetAreaTypeRow = {
      id: createTempId(),
      name: "",
      sortOrder: nextSortOrder,
      isNew: true,
    };
    setAreasOrcamento([...areasOrcamento, newArea]);
    handleEdit("area-type", newArea.id, "");
  };

  const handleSaveArea = async (area: BudgetAreaTypeRow) => {
    const key = buildEditingKey("area-type", area.id);
    const nome = (editingValues[key] ?? area.name).trim();

    if (!nome) {
      setAreasError("O nome da área é obrigatório.");
      if (area.isNew) {
        setAreasOrcamento(
          areasOrcamento.filter((item) => item.id !== area.id),
        );
      }
      clearEditing(key);
      return;
    }

    setAreasError(null);

    try {
      if (area.isNew) {
        const created = await createBudgetAreaType({
          name: nome,
          sortOrder: area.sortOrder,
        });
        setAreasOrcamento((prev) =>
          prev.map((item) =>
            item.id === area.id ? created : item,
          ),
        );
      } else {
        const updated = await updateBudgetAreaType(area.id, {
          name: nome,
          sortOrder: area.sortOrder,
        });
        setAreasOrcamento((prev) =>
          prev.map((item) =>
            item.id === area.id ? updated : item,
          ),
        );
      }
      clearEditing(key);
    } catch {
      setAreasError("Não foi possível guardar a área.");
    }
  };

  const handleCancelArea = (area: BudgetAreaTypeRow) => {
    const key = buildEditingKey("area-type", area.id);
    if (area.isNew) {
      setAreasOrcamento(
        areasOrcamento.filter((item) => item.id !== area.id),
      );
    }
    clearEditing(key);
  };

  const handleDeleteArea = async (id: number) => {
    const item = areasOrcamento.find((area) => area.id === id);
    if (!item) return;

    if (item.isNew) {
      setAreasOrcamento(
        areasOrcamento.filter((area) => area.id !== id),
      );
      return;
    }

    try {
      await deleteBudgetAreaType(id);
      setAreasOrcamento(
        areasOrcamento.filter((area) => area.id !== id),
      );
    } catch {
      setAreasError("Não foi possível eliminar a área.");
    }
  };

  const handleMoveArea = async (id: number, direction: -1 | 1) => {
    const ordered = [...sortedAreas];
    const currentIndex = ordered.findIndex(
      (item) => item.id === id,
    );
    const swapIndex = currentIndex + direction;

    if (currentIndex < 0 || swapIndex < 0) return;
    if (swapIndex >= ordered.length) return;

    const current = ordered[currentIndex];
    const swap = ordered[swapIndex];

    const updatedCurrent = {
      ...current,
      sortOrder: swap.sortOrder,
    };
    const updatedSwap = {
      ...swap,
      sortOrder: current.sortOrder,
    };

    setAreasOrcamento((prev) =>
      prev.map((item) => {
        if (item.id === updatedCurrent.id) return updatedCurrent;
        if (item.id === updatedSwap.id) return updatedSwap;
        return item;
      }),
    );

    try {
      const updates = [];
      if (!updatedCurrent.isNew) {
        updates.push(
          updateBudgetAreaType(updatedCurrent.id, {
            name: updatedCurrent.name,
            sortOrder: updatedCurrent.sortOrder,
          }),
        );
      }
      if (!updatedSwap.isNew) {
        updates.push(
          updateBudgetAreaType(updatedSwap.id, {
            name: updatedSwap.name,
            sortOrder: updatedSwap.sortOrder,
          }),
        );
      }
      await Promise.all(updates);
    } catch {
      setAreasError("Não foi possível atualizar a ordem.");
    }
  };

  const handleAddCapitulo = (areaId: number) => {
    const areaCapitulos = capitulosOrcamento.filter(
      (cap) => cap.budgetAreaTypeId === areaId,
    );
    const nextSortOrder =
      areaCapitulos.reduce(
        (max, item) => Math.max(max, item.sortOrder),
        0,
      ) + 1;

    const newCapitulo: BudgetChapterTypeRow = {
      id: createTempId(),
      budgetAreaTypeId: areaId,
      code: "",
      name: "",
      sortOrder: nextSortOrder,
      isNew: true,
    };

    setCapitulosOrcamento([...capitulosOrcamento, newCapitulo]);
    handleEdit("chapter-type", newCapitulo.id, "");
  };

  const handleSaveCapitulo = async (
    capitulo: BudgetChapterTypeRow,
  ) => {
    const key = buildEditingKey("chapter-type", capitulo.id);
    const nome = (editingValues[key] ?? capitulo.name).trim();

    if (!nome) {
      setCapitulosError("O nome do capítulo é obrigatório.");
      if (capitulo.isNew) {
        setCapitulosOrcamento(
          capitulosOrcamento.filter(
            (item) => item.id !== capitulo.id,
          ),
        );
      }
      clearEditing(key);
      return;
    }

    setCapitulosError(null);

    try {
      if (capitulo.isNew) {
        const created = await createBudgetChapterType({
          budgetAreaTypeId: capitulo.budgetAreaTypeId,
          name: nome,
          code: "",
          sortOrder: capitulo.sortOrder,
        });
        setCapitulosOrcamento((prev) =>
          prev.map((item) =>
            item.id === capitulo.id ? created : item,
          ),
        );
      } else {
        const updated = await updateBudgetChapterType(
          capitulo.id,
          {
            budgetAreaTypeId: capitulo.budgetAreaTypeId,
            name: nome,
            code: capitulo.code,
            sortOrder: capitulo.sortOrder,
          },
        );
        setCapitulosOrcamento((prev) =>
          prev.map((item) =>
            item.id === capitulo.id ? updated : item,
          ),
        );
      }
      clearEditing(key);
    } catch {
      setCapitulosError(
        "Não foi possível guardar o capítulo.",
      );
    }
  };

  const handleCancelCapitulo = (capitulo: BudgetChapterTypeRow) => {
    const key = buildEditingKey("chapter-type", capitulo.id);
    if (capitulo.isNew) {
      setCapitulosOrcamento(
        capitulosOrcamento.filter(
          (item) => item.id !== capitulo.id,
        ),
      );
    }
    clearEditing(key);
  };

  const handleDeleteCapitulo = async (id: number) => {
    const item = capitulosOrcamento.find((cap) => cap.id === id);
    if (!item) return;

    if (item.isNew) {
      setCapitulosOrcamento(
        capitulosOrcamento.filter((cap) => cap.id !== id),
      );
      return;
    }

    try {
      await deleteBudgetChapterType(id);
      setCapitulosOrcamento(
        capitulosOrcamento.filter((cap) => cap.id !== id),
      );
    } catch {
      setCapitulosError(
        "Não foi possível eliminar o capítulo.",
      );
    }
  };

  const handleMoveCapitulo = async (
    id: number,
    areaId: number,
    direction: -1 | 1,
  ) => {
    const capitulosDaArea = sortedCapitulos.filter(
      (cap) => cap.budgetAreaTypeId === areaId,
    );
    const currentIndex = capitulosDaArea.findIndex(
      (cap) => cap.id === id,
    );
    const swapIndex = currentIndex + direction;

    if (currentIndex < 0 || swapIndex < 0) return;
    if (swapIndex >= capitulosDaArea.length) return;

    const current = capitulosDaArea[currentIndex];
    const swap = capitulosDaArea[swapIndex];

    const updatedCurrent = {
      ...current,
      sortOrder: swap.sortOrder,
    };
    const updatedSwap = {
      ...swap,
      sortOrder: current.sortOrder,
    };

    setCapitulosOrcamento((prev) =>
      prev.map((item) => {
        if (item.id === updatedCurrent.id) return updatedCurrent;
        if (item.id === updatedSwap.id) return updatedSwap;
        return item;
      }),
    );

    try {
      const updates = [];
      if (!updatedCurrent.isNew) {
        updates.push(
          updateBudgetChapterType(updatedCurrent.id, {
            budgetAreaTypeId: updatedCurrent.budgetAreaTypeId,
            name: updatedCurrent.name,
            code: updatedCurrent.code,
            sortOrder: updatedCurrent.sortOrder,
          }),
        );
      }
      if (!updatedSwap.isNew) {
        updates.push(
          updateBudgetChapterType(updatedSwap.id, {
            budgetAreaTypeId: updatedSwap.budgetAreaTypeId,
            name: updatedSwap.name,
            code: updatedSwap.code,
            sortOrder: updatedSwap.sortOrder,
          }),
        );
      }
      await Promise.all(updates);
    } catch {
      setCapitulosError(
        "Não foi possível atualizar a ordem dos capítulos.",
      );
    }
  };

  if (!isOpen && !isAnimating) return null;

  const getTabTitle = () => {
    switch (activeTab) {
      case "general":
        return "Geral";
      case "notifications":
        return "Notificações";
      case "dados":
        return "Dados";
      case "empresas":
        return "Empresas";
      case "tipos-obras":
        return "Tipos de Obras";
      case "areas-orcamento":
        return "Áreas de Orçamento";
      case "capitulos-orcamento":
        return "Capítulos de Orçamento";
      case "margin-alerts":
        return "Semáforo de Margens";
      case "linhas-percentuais":
        return "Linhas Percentuais";
      case "classificacao":
        return "Classificação";
      case "plano-categorias":
        return "Plano de Categorias";
      case "categorias-despesa":
        return "Regras de Categorias";
      case "anomalias":
        return "Anomalias";
      case "financeiro":
        return "Parâmetros Financeiros";
      case "bancos":
        return "Bancos";
      case "estados-pagamento":
        return "Estados de Pagamento";
      case "custos-indiretos":
        return "Custos Indiretos";
      case "credit-control":
        return "Controlo de Crédito";
      case "treasury":
        return "Tesouraria";
      case "ia":
        return "Inteligência Artificial";
      default:
        return "Geral";
    }
  };

  const themeTiles = [
    {
      id: "system" as const,
      label: "Usar configurações do sistema",
      subtitle: "O sistema decide o tema (claro ou escuro)",
      icon: (
        <img
          src={themeSystemIcon}
          alt="System theme"
          className="w-full h-20 rounded object-cover"
          style={{ borderRadius: "var(--radius)" }}
        />
      ),
    },
    {
      id: "light" as const,
      label: "Claro",
      subtitle: "Tema em modo claro",
      icon: (
        <div
          className="w-full h-20 bg-[#FAFAFA] rounded border border-[#E1DFDD] flex items-center justify-center"
          style={{ borderRadius: "var(--radius)" }}
        >
          <div
            className="w-16 h-12 bg-white border border-[#E1DFDD] rounded shadow-sm"
            style={{
              borderRadius: "calc(var(--radius) - 2px)",
            }}
          ></div>
        </div>
      ),
    },
    {
      id: "dark" as const,
      label: "Escuro",
      subtitle: "Tema em modo escuro",
      icon: (
        <div
          className="w-full h-20 bg-[#1B1B1B] rounded border border-[#3D3D3D] flex items-center justify-center"
          style={{ borderRadius: "var(--radius)" }}
        >
          <div
            className="w-16 h-12 bg-[#222222] border border-[#3D3D3D] rounded shadow-sm"
            style={{
              borderRadius: "calc(var(--radius) - 2px)",
            }}
          ></div>
        </div>
      ),
    },
  ];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-200 p-0 md:p-4 ${
        isAnimating && isOpen ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-card w-full h-full md:h-[680px] flex flex-col md:flex-row overflow-hidden transition-all duration-200 ${
          isAnimating && isOpen
            ? "opacity-100 scale-100"
            : "opacity-0 scale-95"
        } md:border md:border-border`}
        style={{
          borderRadius: "0",
          boxShadow: "var(--elevation-sm)",
          maxWidth: 1100,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Desktop Sidebar - Hidden on Mobile */}
        <div className="hidden md:flex md:w-80 bg-muted border-r border-border p-4 flex-col overflow-y-auto">
          <div className="flex items-center gap-2 mb-6 px-2">
            <h3>Configurações</h3>
          </div>

          <nav className="space-y-5">
            {SETTINGS_NAV_SECTIONS.map((section, sectionIndex) => (
              <div key={section.title ?? sectionIndex}>
                {section.title && (
                  <p
                    className="px-3 mb-1 uppercase tracking-wider text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {section.title}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <button
                      key={item.tab}
                      onClick={() => setActiveTab(item.tab)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                        activeTab === item.tab
                          ? "bg-accent text-accent-foreground"
                          : "text-foreground hover:bg-accent/50"
                      }`}
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      <item.icon className="w-5 h-5" />
                      <span
                        className="whitespace-nowrap"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border shrink-0">
            <h2 className="hidden md:block">{getTabTitle()}</h2>

            {/* Mobile Dropdown/Title */}
            <h2 className="md:hidden">{getTabTitle()}</h2>

            <button
              onClick={handleClose}
              className="p-2.5 md:p-1.5 rounded hover:bg-accent text-foreground transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
              aria-label="Close settings"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              <Dismiss24Regular className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {activeTab === "general" && (
              <div className="space-y-6 md:space-y-8">
                {/* About Section */}
                <div>
                  <button
                    className="w-full flex items-center justify-between p-4 bg-secondary hover:bg-accent border border-border rounded transition-colors group min-h-[60px]"
                    style={{
                      borderRadius: "var(--radius-card)",
                    }}
                  >
                    <div className="text-left">
                      <p
                        style={{
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                        }}
                      >
                        Acerca do AJA2 IA
                      </p>
                      <p
                        className="text-secondary-text mt-0.5"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Versão 1.0.0
                      </p>
                    </div>
                    <span className="text-primary">&rsaquo;</span>
                  </button>
                </div>

                {/* Appearance Section */}
                <div>
                  <h4 className="mb-4">Aparência</h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                    {themeTiles.map((tile) => (
                      <button
                        key={tile.id}
                        onClick={() => onThemeModeChange(tile.id)}
                        className={`relative p-4 bg-card border-2 rounded transition-all hover:border-primary/50 ${
                          themeMode === tile.id
                            ? "border-primary shadow-sm"
                            : "border-border"
                        }`}
                        style={{
                          borderRadius: "var(--radius-card)",
                        }}
                      >
                        {/* Selection indicator */}
                        {themeMode === tile.id && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-primary-foreground"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}

                        {/* Preview */}
                        <div className="mb-3">{tile.icon}</div>

                        {/* Label */}
                        <div className="text-left">
                          <p
                            style={{
                              fontWeight:
                                "var(--font-weight-semi-bold)",
                              fontSize: "var(--text-base)",
                            }}
                          >
                            {tile.label}
                          </p>
                          <p
                            className="text-secondary-text mt-0.5"
                            style={{
                              fontSize: "var(--text-caption)",
                            }}
                          >
                            {tile.subtitle}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Terms of use */}
                <div>
                  <h4 className="mb-2">Termos de utilização</h4>
                  <button
                    className="text-primary hover:underline flex items-center gap-1 min-h-[44px]"
                    style={{ fontSize: "var(--text-base)" }}
                  >
                    Ver
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "notifications" && (
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 md:p-8 rounded text-center"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <Alert24Regular className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h4 className="mb-2">Definições de Notificações</h4>
                  <p className="text-secondary-text">
                    Configure as suas preferências de notificações aqui
                  </p>
                </div>
              </div>
            )}

            {activeTab === "dados" && (
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 md:p-8 rounded"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h4 className="mb-2">Limite de Sugestões</h4>
                      <p className="text-secondary-text">
                        Define o número máximo de sugestões mostradas no modal.
                        Deixa vazio para usar o padrão da aplicação.
                      </p>
                    </div>
                    <Database24Regular className="w-8 h-8 text-muted-foreground" />
                  </div>

                  {suggestionLimitError && (
                    <div
                      className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {suggestionLimitError}
                    </div>
                  )}

                  {suggestionLimitSuccess && (
                    <div
                      className="mb-4 border border-success/30 bg-success/10 text-success px-4 py-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {suggestionLimitSuccess}
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-end gap-3">
                    <div className="flex-1">
                      <label
                        htmlFor="suggestion-limit"
                        className="block text-foreground mb-2"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        Limite
                      </label>
                      <input
                        id="suggestion-limit"
                        type="number"
                        min={1}
                        value={suggestionLimitValue}
                        onChange={(event) =>
                          setSuggestionLimitValue(event.target.value)
                        }
                        placeholder="Padrão"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        style={{ borderRadius: "var(--radius)" }}
                        disabled={suggestionLimitLoading}
                      />
                    </div>
                    <button
                      onClick={handleSaveSuggestionLimit}
                      disabled={suggestionLimitLoading || suggestionLimitSaving}
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      {suggestionLimitSaving
                        ? "A guardar..."
                        : "Guardar limite"}
                    </button>
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 md:p-8 rounded"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h4 className="mb-2">Azure Search</h4>
                      <p className="text-secondary-text">
                        Reindexa todas as linhas de orçamento no Azure Search. Use
                        quando os dados estiverem desatualizados.
                      </p>
                    </div>
                    <Database24Regular className="w-8 h-8 text-muted-foreground" />
                  </div>

                  {reindexError && (
                    <div
                      className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {reindexError}
                    </div>
                  )}

                  {reindexSuccess && (
                    <div
                      className="mb-4 border border-success/30 bg-success/10 text-success px-4 py-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {reindexSuccess}
                    </div>
                  )}

                  <button
                    onClick={handleReindexBudgetLines}
                    disabled={reindexLoading}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    {reindexLoading
                      ? "A reindexar..."
                      : "Reindexar Orçamentos"}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "tipos-obras" && (
              <div>
                {/* Header with Add Button */}
                <div className="flex items-center justify-between mb-4">
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Gerir os tipos de obras disponíveis no sistema
                  </p>
                  <button
                    onClick={handleAddTipoObra}
                    disabled={isLoading}
                    className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-caption)",
                    }}
                  >
                    <Add20Regular className="w-4 h-4" />
                    Adicionar
                  </button>
                </div>

                {loadError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {loadError}
                  </div>
                )}

                {workTypesError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {workTypesError}
                  </div>
                )}

                {/* Table */}
                <div
                  className="border border-border rounded"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <table className="w-full">
                    <thead>
                      <tr
                        className="border-b border-border bg-secondary/50"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        <th
                          className="text-left p-4 text-foreground"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          Nome
                        </th>
                        <th
                          className="text-right p-4 text-foreground"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                            width: "200px",
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWorkTypes.map((tipo, index) => {
                        const key = buildEditingKey(
                          "work-type",
                          tipo.id,
                        );
                        const isEditing = editingKey === key;
                        const editingValue =
                          editingValues[key] ?? tipo.name;

                        return (
                          <tr
                            key={tipo.id}
                            className="border-b border-border hover:bg-secondary/30 transition-colors last:border-b-0"
                          >
                            <td className="p-4">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(event) =>
                                    setEditingValues((prev) => ({
                                      ...prev,
                                      [key]: event.target.value,
                                    }))
                                  }
                                  placeholder="Nome do tipo de obra"
                                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                  style={{
                                    borderRadius:
                                      "var(--radius-button)",
                                    fontSize: "var(--text-base)",
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className="text-foreground"
                                  style={{
                                    fontSize: "var(--text-base)",
                                  }}
                                >
                                  {tipo.name}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-2">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleSaveTipoObra(tipo)
                                      }
                                      className="p-2 hover:bg-success/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Guardar"
                                    >
                                      <Checkmark20Regular className="w-5 h-5 text-success" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleCancelTipoObra(tipo)
                                      }
                                      className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Cancelar"
                                    >
                                      <Dismiss20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleEdit(
                                          "work-type",
                                          tipo.id,
                                          tipo.name,
                                        )
                                      }
                                      className="p-2 hover:bg-secondary rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Editar"
                                    >
                                      <Edit20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteTipoObra(tipo.id)
                                      }
                                      className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Eliminar"
                                    >
                                      <Delete20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleMoveTipoObra(
                                          tipo.id,
                                          -1,
                                        )
                                      }
                                      disabled={index === 0}
                                      className={`p-2 rounded transition-colors ${
                                        index === 0
                                          ? "opacity-30 cursor-not-allowed"
                                          : "hover:bg-secondary"
                                      }`}
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Mover para cima"
                                    >
                                      <ArrowUp20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleMoveTipoObra(
                                          tipo.id,
                                          1,
                                        )
                                      }
                                      disabled={
                                        index ===
                                        sortedWorkTypes.length - 1
                                      }
                                      className={`p-2 rounded transition-colors ${
                                        index ===
                                        sortedWorkTypes.length - 1
                                          ? "opacity-30 cursor-not-allowed"
                                          : "hover:bg-secondary"
                                      }`}
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Mover para baixo"
                                    >
                                      <ArrowDown20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {sortedWorkTypes.length === 0 && !isLoading && (
                    <div className="p-12 text-center">
                      <p
                        className="text-secondary-text"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                        Nenhum tipo de obra registado. Clique em
                        "Adicionar" para criar o primeiro.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "areas-orcamento" && (
              <div>
                {/* Header with Add Button */}
                <div className="flex items-center justify-between mb-4">
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Gerir as áreas principais de organização de orçamentos
                  </p>
                  <button
                    onClick={handleAddArea}
                    disabled={isLoading}
                    className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-caption)",
                    }}
                  >
                    <Add20Regular className="w-4 h-4" />
                    Adicionar
                  </button>
                </div>

                {loadError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {loadError}
                  </div>
                )}

                {areasError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {areasError}
                  </div>
                )}

                {/* Table */}
                <div
                  className="border border-border rounded"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <table className="w-full">
                    <thead>
                      <tr
                        className="border-b border-border bg-secondary/50"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        <th
                          className="text-left p-4 text-foreground"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          Nome
                        </th>
                        <th
                          className="text-right p-4 text-foreground"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                            width: "200px",
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAreas.map((area, index) => {
                        const key = buildEditingKey(
                          "area-type",
                          area.id,
                        );
                        const isEditing = editingKey === key;
                        const editingValue =
                          editingValues[key] ?? area.name;

                        return (
                          <tr
                            key={area.id}
                            className="border-b border-border hover:bg-secondary/30 transition-colors last:border-b-0"
                          >
                            <td className="p-4">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(event) =>
                                    setEditingValues((prev) => ({
                                      ...prev,
                                      [key]: event.target.value,
                                    }))
                                  }
                                  placeholder="Nome da área"
                                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                  style={{
                                    borderRadius:
                                      "var(--radius-button)",
                                    fontSize: "var(--text-base)",
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className="text-foreground"
                                  style={{
                                    fontSize: "var(--text-base)",
                                  }}
                                >
                                  {area.name}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-2">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleSaveArea(area)
                                      }
                                      className="p-2 hover:bg-success/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Guardar"
                                    >
                                      <Checkmark20Regular className="w-5 h-5 text-success" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleCancelArea(area)
                                      }
                                      className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Cancelar"
                                    >
                                      <Dismiss20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleEdit(
                                          "area-type",
                                          area.id,
                                          area.name,
                                        )
                                      }
                                      className="p-2 hover:bg-secondary rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Editar"
                                    >
                                      <Edit20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteArea(area.id)
                                      }
                                      className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Eliminar"
                                    >
                                      <Delete20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleMoveArea(area.id, -1)
                                      }
                                      disabled={index === 0}
                                      className={`p-2 rounded transition-colors ${
                                        index === 0
                                          ? "opacity-30 cursor-not-allowed"
                                          : "hover:bg-secondary"
                                      }`}
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Mover para cima"
                                    >
                                      <ArrowUp20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleMoveArea(area.id, 1)
                                      }
                                      disabled={
                                        index ===
                                        sortedAreas.length - 1
                                      }
                                      className={`p-2 rounded transition-colors ${
                                        index ===
                                        sortedAreas.length - 1
                                          ? "opacity-30 cursor-not-allowed"
                                          : "hover:bg-secondary"
                                      }`}
                                      style={{
                                        borderRadius:
                                          "var(--radius-button)",
                                      }}
                                      title="Mover para baixo"
                                    >
                                      <ArrowDown20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {sortedAreas.length === 0 && !isLoading && (
                    <div className="p-12 text-center">
                      <p
                        className="text-secondary-text"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                        Nenhuma área de orçamento registada. Clique em
                        "Adicionar" para criar a primeira.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "capitulos-orcamento" && (
              <div>
                <p
                  className="text-secondary-text mb-4"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  Gerir os capítulos organizados por área de orçamento
                </p>

                {loadError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {loadError}
                  </div>
                )}

                {capitulosError && (
                  <div
                    className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {capitulosError}
                  </div>
                )}

                {/* Grouped by Area */}
                <div className="space-y-4">
                  {sortedAreas.map((area) => {
                    const capitulosDaArea = sortedCapitulos.filter(
                      (cap) => cap.budgetAreaTypeId === area.id,
                    );
                    const canAddCapitulo = !isLoading && !area.isNew;

                    return (
                      <div
                        key={area.id}
                        className="border border-border rounded overflow-hidden"
                        style={{
                          borderRadius: "var(--radius-card)",
                        }}
                      >
                        {/* Area Header with Add Button */}
                        <div className="bg-secondary/30 px-4 py-3 flex items-center justify-between border-b border-border">
                          <span
                            className="text-foreground"
                            style={{
                              fontSize: "var(--text-base)",
                              fontWeight:
                                "var(--font-weight-semi-bold)",
                            }}
                          >
                            {area.name}
                          </span>
                          <button
                            onClick={() => handleAddCapitulo(area.id)}
                            disabled={!canAddCapitulo}
                            className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              borderRadius: "var(--radius-button)",
                              fontSize: "var(--text-caption)",
                            }}
                          >
                            <Add20Regular className="w-4 h-4" />
                            Adicionar
                          </button>
                        </div>

                        {/* Capítulos Table */}
                        {capitulosDaArea.length > 0 ? (
                          <table className="w-full">
                            <thead>
                              <tr
                                className="border-b border-border bg-secondary/10"
                                style={{ fontSize: "var(--text-caption)" }}
                              >
                                <th
                                  className="text-left p-4 text-foreground"
                                  style={{
                                    fontWeight:
                                      "var(--font-weight-semi-bold)",
                                  }}
                                >
                                  Nome
                                </th>
                                <th
                                  className="text-right p-4 text-foreground"
                                  style={{
                                    fontWeight:
                                      "var(--font-weight-semi-bold)",
                                    width: "200px",
                                  }}
                                >
                                  Ações
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {capitulosDaArea.map((capitulo, index) => {
                                const key = buildEditingKey(
                                  "chapter-type",
                                  capitulo.id,
                                );
                                const isEditing =
                                  editingKey === key;
                                const editingValue =
                                  editingValues[key] ??
                                  capitulo.name;

                                return (
                                  <tr
                                    key={capitulo.id}
                                    className="border-b border-border hover:bg-secondary/20 transition-colors last:border-b-0"
                                  >
                                    <td className="p-4">
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={editingValue}
                                          onChange={(event) =>
                                            setEditingValues((prev) => ({
                                              ...prev,
                                              [key]: event.target.value,
                                            }))
                                          }
                                          placeholder="Nome do capítulo"
                                          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                          style={{
                                            borderRadius:
                                              "var(--radius-button)",
                                            fontSize: "var(--text-base)",
                                          }}
                                          autoFocus
                                        />
                                      ) : (
                                        <span
                                          className="text-foreground"
                                          style={{
                                            fontSize: "var(--text-base)",
                                          }}
                                        >
                                          {capitulo.name}
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-4">
                                      <div className="flex items-center justify-end gap-2">
                                        {isEditing ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleSaveCapitulo(capitulo)
                                              }
                                              className="p-2 hover:bg-success/10 rounded transition-colors"
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Guardar"
                                            >
                                              <Checkmark20Regular className="w-5 h-5 text-success" />
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleCancelCapitulo(capitulo)
                                              }
                                              className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Cancelar"
                                            >
                                              <Dismiss20Regular className="w-5 h-5 text-destructive" />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleEdit(
                                                  "chapter-type",
                                                  capitulo.id,
                                                  capitulo.name,
                                                )
                                              }
                                              className="p-2 hover:bg-secondary rounded transition-colors"
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Editar"
                                            >
                                              <Edit20Regular className="w-5 h-5 text-foreground" />
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleDeleteCapitulo(
                                                  capitulo.id,
                                                )
                                              }
                                              className="p-2 hover:bg-destructive/10 rounded transition-colors"
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Eliminar"
                                            >
                                              <Delete20Regular className="w-5 h-5 text-destructive" />
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleMoveCapitulo(
                                                  capitulo.id,
                                                  area.id,
                                                  -1,
                                                )
                                              }
                                              disabled={index === 0}
                                              className={`p-2 rounded transition-colors ${
                                                index === 0
                                                  ? "opacity-30 cursor-not-allowed"
                                                  : "hover:bg-secondary"
                                              }`}
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Mover para cima"
                                            >
                                              <ArrowUp20Regular className="w-5 h-5 text-foreground" />
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleMoveCapitulo(
                                                  capitulo.id,
                                                  area.id,
                                                  1,
                                                )
                                              }
                                              disabled={
                                                index ===
                                                capitulosDaArea.length - 1
                                              }
                                              className={`p-2 rounded transition-colors ${
                                                index ===
                                                capitulosDaArea.length - 1
                                                  ? "opacity-30 cursor-not-allowed"
                                                  : "hover:bg-secondary"
                                              }`}
                                              style={{
                                                borderRadius:
                                                  "var(--radius-button)",
                                              }}
                                              title="Mover para baixo"
                                            >
                                              <ArrowDown20Regular className="w-5 h-5 text-foreground" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <div className="p-8 text-center">
                            <p
                              className="text-secondary-text"
                              style={{ fontSize: "var(--text-caption)" }}
                            >
                              Nenhum capítulo nesta área. Clique em "Adicionar"
                              para criar o primeiro.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {sortedAreas.length === 0 && !isLoading && (
                    <div
                      className="p-12 text-center border border-border rounded"
                      style={{ borderRadius: "var(--radius-card)" }}
                    >
                      <p
                        className="text-secondary-text"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                        Nenhuma área de orçamento disponível. Crie áreas na tab
                        "Áreas de Orçamento" primeiro.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "margin-alerts" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                {marginRangesError && (
                  <p className="text-destructive text-sm">{marginRangesError}</p>
                )}
                {marginRangesSuccess && (
                  <p className="text-sm" style={{ color: "var(--color-success, #16a34a)" }}>{marginRangesSuccess}</p>
                )}
                {marginRangesLoading ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : (
                  <>
                    <p className="text-secondary-text text-sm">
                      Define intervalos de margem com cores. O círculo colorido aparece junto à percentagem de margem no resumo do orçamento.
                    </p>

                    <div className="space-y-2">
                      {marginRangesDraft.map((range, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 border border-border rounded"
                          style={{ borderRadius: "var(--radius-card)" }}
                        >
                          <input
                            type="color"
                            value={range.color}
                            onChange={(e) => handleMarginRangeChange(index, "color", e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border border-border flex-shrink-0"
                            title="Cor"
                          />
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              type="number"
                              value={range.minPercent}
                              onChange={(e) => handleMarginRangeChange(index, "minPercent", parseFloat(e.target.value) || 0)}
                              className="w-16 text-right border border-border rounded px-2 py-1 bg-background text-foreground"
                              style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                              placeholder="Min"
                              step="0.5"
                            />
                            <span className="text-secondary-text text-sm">%</span>
                          </div>
                          <span className="text-secondary-text text-sm flex-shrink-0">até</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              type="number"
                              value={range.maxPercent ?? ""}
                              onChange={(e) => handleMarginRangeChange(index, "maxPercent", e.target.value === "" ? null : parseFloat(e.target.value))}
                              className="w-16 text-right border border-border rounded px-2 py-1 bg-background text-foreground"
                              style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                              placeholder="∞"
                              step="0.5"
                            />
                            <span className="text-secondary-text text-sm flex-shrink-0">%</span>
                          </div>
                          <input
                            type="text"
                            value={range.label ?? ""}
                            onChange={(e) => handleMarginRangeChange(index, "label", e.target.value || null)}
                            className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground min-w-0"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                            placeholder="Etiqueta (opcional)"
                          />
                          <button
                            onClick={() => handleRemoveMarginRange(index)}
                            className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Remover"
                          >
                            <Delete20Regular className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {marginRangesDraft.length === 0 && (
                      <div
                        className="p-8 text-center border border-border rounded"
                        style={{ borderRadius: "var(--radius-card)" }}
                      >
                        <p className="text-secondary-text text-sm">
                          Nenhum alerta configurado. Adiciona um intervalo para começar.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <button
                        onClick={handleAddMarginRange}
                        className="flex items-center gap-2 px-3 py-2 border border-border rounded hover:bg-accent/50 transition-colors text-foreground"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        <Add20Regular className="w-4 h-4" />
                        Adicionar intervalo
                      </button>
                      <button
                        onClick={handleSaveMarginRanges}
                        disabled={marginRangesSaving}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        {marginRangesSaving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "linhas-percentuais" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                {/* Default suggestion margin */}
                <div
                  className="p-4 border border-border rounded space-y-2"
                  style={{ borderRadius: "var(--radius-card)", backgroundColor: "var(--accent)" }}
                >
                  <div>
                    <p className="text-foreground" style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semi-bold)" }}>
                      Margem default para sugestões de preço
                    </p>
                    <p className="text-secondary-text mt-1" style={{ fontSize: "var(--text-caption)", lineHeight: "1.5" }}>
                      Aplicada quando aceitas uma sugestão de preço de custo e a linha não tem margem definida.
                      Valor de 120 = preço de venda equivale a 1.20 × custo (20% lucro sobre o custo).
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={defaultMarginInput}
                      onChange={(e) => setDefaultMarginInput(parseFloat(e.target.value) || 0)}
                      className="w-28 text-right border border-border rounded px-2 py-1 bg-background text-foreground"
                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      min="0"
                      max="500"
                      step="1"
                    />
                    <span className="text-secondary-text" style={{ fontSize: "var(--text-sm)" }}>%</span>
                    <button
                      onClick={async () => {
                        if (!Number.isFinite(defaultMarginInput) || defaultMarginInput <= 0) return;
                        setDefaultMarginSaving(true);
                        try {
                          await saveAiSettings({ defaultSuggestionMarginPercent: defaultMarginInput });
                        } finally {
                          setDefaultMarginSaving(false);
                        }
                      }}
                      disabled={defaultMarginSaving || !Number.isFinite(defaultMarginInput) || defaultMarginInput <= 0}
                      className="px-3 py-1.5 bg-primary text-primary-foreground rounded transition-colors hover:bg-primary/90 disabled:opacity-50"
                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                    >
                      {defaultMarginSaving ? "A guardar..." : "Guardar"}
                    </button>
                  </div>
                </div>

                {percentageRulesError && (
                  <p className="text-destructive text-sm">{percentageRulesError}</p>
                )}
                {percentageRulesSuccess && (
                  <p className="text-sm" style={{ color: "var(--color-success, #16a34a)" }}>{percentageRulesSuccess}</p>
                )}
                {percentageRulesLoading ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : (
                  <>
                    <p className="text-secondary-text text-sm">
                      Define artigos cujo preço é uma percentagem do total das outras linhas do orçamento (ex.: Montagem de Estaleiro). Quando o utilizador escrever a descrição num orçamento, a regra correspondente é aplicada automaticamente.
                    </p>

                    <div className="space-y-2">
                      {percentageRulesDraft.map((rule, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 border border-border rounded"
                          style={{ borderRadius: "var(--radius-card)" }}
                        >
                          <input
                            type="text"
                            value={rule.description}
                            onChange={(e) => handlePercentageRuleChange(index, "description", e.target.value)}
                            className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground min-w-0"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                            placeholder="Descrição (ex.: Montagem de Estaleiro)"
                          />
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              type="number"
                              value={rule.defaultPercentage}
                              onChange={(e) => handlePercentageRuleChange(index, "defaultPercentage", parseFloat(e.target.value) || 0)}
                              className="w-20 text-right border border-border rounded px-2 py-1 bg-background text-foreground"
                              style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <span className="text-secondary-text text-sm">%</span>
                          </div>
                          <button
                            onClick={() => handleRemovePercentageRule(index)}
                            className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Remover"
                          >
                            <Delete20Regular className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {percentageRulesDraft.length === 0 && (
                      <div
                        className="p-8 text-center border border-border rounded"
                        style={{ borderRadius: "var(--radius-card)" }}
                      >
                        <p className="text-secondary-text text-sm">
                          Nenhuma regra configurada. Adiciona uma para começar.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <button
                        onClick={handleAddPercentageRule}
                        className="flex items-center gap-2 px-3 py-2 border border-border rounded hover:bg-accent/50 transition-colors text-foreground"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        <Add20Regular className="w-4 h-4" />
                        Adicionar regra
                      </button>
                      <button
                        onClick={handleSavePercentageRules}
                        disabled={percentageRulesSaving}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        {percentageRulesSaving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "classificacao" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                {classificationRulesError && (
                  <p className="text-destructive text-sm">{classificationRulesError}</p>
                )}
                {classificationRulesSuccess && (
                  <p className="text-sm" style={{ color: "var(--color-success, #16a34a)" }}>{classificationRulesSuccess}</p>
                )}
                {classificationRulesLoading ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : (
                  <>
                    <p className="text-secondary-text text-sm">
                      Define regras que sugerem se um artigo é Material (Fornecedor) ou Trabalho (Subempreiteiro). A primeira regra que casa decide; a classificação manual de um artigo prevalece sempre.
                    </p>

                    <div className="space-y-2">
                      {classificationRulesDraft.map((rule, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 border border-border rounded flex-wrap"
                          style={{ borderRadius: "var(--radius-card)" }}
                        >
                          <select
                            value={rule.matchType}
                            onChange={(e) => handleClassificationRuleChange(index, "matchType", e.target.value)}
                            className="border border-border rounded px-2 py-1 bg-background text-foreground flex-shrink-0"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                          >
                            <option value="category">Categoria</option>
                            <option value="keyword">Palavra-chave</option>
                          </select>
                          <input
                            type="text"
                            value={rule.matchValue}
                            onChange={(e) => handleClassificationRuleChange(index, "matchValue", e.target.value)}
                            className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "12rem" }}
                            placeholder="categoria ou palavra a procurar na designação"
                          />
                          <select
                            value={rule.classificationCode}
                            onChange={(e) => handleClassificationRuleChange(index, "classificationCode", e.target.value)}
                            className="border border-border rounded px-2 py-1 bg-background text-foreground flex-shrink-0"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                          >
                            <option value="material">Material (Fornecedor)</option>
                            <option value="trabalho">Trabalho (Subempreiteiro)</option>
                          </select>
                          <button
                            onClick={() => handleRemoveClassificationRule(index)}
                            className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Remover"
                          >
                            <Delete20Regular className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {classificationRulesDraft.length === 0 && (
                      <div
                        className="p-8 text-center border border-border rounded"
                        style={{ borderRadius: "var(--radius-card)" }}
                      >
                        <p className="text-secondary-text text-sm">
                          Nenhuma regra configurada. Adiciona uma para começar.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between" style={{ paddingTop: "0.5rem" }}>
                      <button
                        onClick={handleAddClassificationRule}
                        className="flex items-center gap-2 px-3 py-2 border border-border rounded hover:bg-accent/50 transition-colors text-foreground"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        <Add20Regular className="w-4 h-4" />
                        Adicionar regra
                      </button>
                      <button
                        onClick={handleSaveClassificationRules}
                        disabled={classificationRulesSaving}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        {classificationRulesSaving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "plano-categorias" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                <p className="text-secondary-text text-sm">
                  Organiza as categorias de despesa em dois níveis: categorias-raiz e subcategorias.
                  O código de cada categoria tem de ser único. As categorias aqui definidas ficam
                  disponíveis nas faturas e nas regras de categorização.
                </p>
                <p className="text-secondary-text text-sm">
                  O <strong>tipo de custo</strong> decide se a despesa pode ser imputada a uma obra.
                  Numa categoria <strong>de estrutura</strong> (combustíveis, estaleiro, seguros) a
                  leitura automática de documentos nunca atribui obra — a despesa fica da empresa,
                  mesmo que a leitura proponha uma. Nas categorias <strong>de obra</strong> nada muda.
                  As que ficam <strong>por classificar</strong> comportam-se como hoje.
                </p>

                {categoryTreeError && (
                  <div
                    className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {categoryTreeError}
                  </div>
                )}

                {/* Criar categoria-raiz */}
                <div
                  className="flex items-end gap-3 p-3 border border-border rounded flex-wrap"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <label className="flex flex-col gap-1 text-sm text-secondary-text flex-1" style={{ minWidth: "10rem" }}>
                    Nova categoria
                    <input
                      type="text"
                      value={newRootName}
                      onChange={(e) => {
                        setNewRootName(e.target.value);
                        if (!newRootCodeTouched) setNewRootCode(slugifyCategoryName(e.target.value));
                      }}
                      placeholder="Nome da categoria"
                      className="border border-border rounded px-2 py-1 bg-background text-foreground"
                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-secondary-text" style={{ minWidth: "8rem" }}>
                    Código
                    <input
                      type="text"
                      value={newRootCodeTouched ? newRootCode : slugifyCategoryName(newRootName)}
                      onChange={(e) => {
                        setNewRootCodeTouched(true);
                        setNewRootCode(e.target.value);
                      }}
                      placeholder="codigo"
                      className="border border-border rounded px-2 py-1 bg-background text-foreground"
                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-secondary-text">
                    Tipo de custo
                    <CostTypeSelect value={newRootCostType} onChange={setNewRootCostType} />
                  </label>
                  <button
                    onClick={handleCreateRootCategory}
                    disabled={categoryBusy || !newRootName.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                  >
                    <Add20Regular className="w-4 h-4" />
                    Adicionar categoria
                  </button>
                </div>

                {categoryTreeLoading ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : sortedCategoryRoots.length === 0 ? (
                  <div
                    className="p-8 text-center border border-border rounded"
                    style={{ borderRadius: "var(--radius-card)" }}
                  >
                    <p className="text-secondary-text text-sm">
                      Nenhuma categoria configurada. Adiciona uma para começar.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sortedCategoryRoots.map(({ root, children }) => (
                      <div
                        key={root.id}
                        className="border border-border rounded"
                        style={{ borderRadius: "var(--radius-card)" }}
                      >
                        {/* Linha da categoria-raiz */}
                        <div className="flex items-center gap-3 p-3 flex-wrap">
                          {editingCategoryId === root.id ? (
                            <>
                              <input
                                type="text"
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                                className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground"
                                style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "10rem" }}
                                placeholder="Nome"
                              />
                              <input
                                type="text"
                                value={editCategoryCode}
                                onChange={(e) => setEditCategoryCode(e.target.value)}
                                className="border border-border rounded px-2 py-1 bg-background text-foreground"
                                style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", width: "9rem" }}
                                placeholder="codigo"
                              />
                              <CostTypeSelect
                                value={editCategoryCostType}
                                onChange={setEditCategoryCostType}
                              />
                              <button
                                onClick={() => handleSaveCategoryEdit(root)}
                                disabled={categoryBusy}
                                className="p-1.5 hover:bg-accent rounded transition-colors flex-shrink-0 disabled:opacity-50"
                                style={{ borderRadius: "var(--radius-button)" }}
                                title="Guardar"
                              >
                                <Checkmark20Regular className="w-5 h-5 text-green-700" />
                              </button>
                              <button
                                onClick={() => setEditingCategoryId(null)}
                                className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                                style={{ borderRadius: "var(--radius-button)" }}
                                title="Cancelar"
                              >
                                <Dismiss20Regular className="w-5 h-5 text-destructive" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span
                                className="flex-1 text-foreground"
                                style={{ minWidth: "8rem" }}
                              >
                                <span style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semi-bold)" }}>
                                  {root.name}
                                </span>
                                <span className="block text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                                  {root.code}
                                </span>
                              </span>
                              {costTypeBadge(root.isStructural)}
                              <button
                                onClick={() => startAddSubcategory(root.id)}
                                className="flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-accent/50 transition-colors text-foreground flex-shrink-0"
                                style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                              >
                                <Add20Regular className="w-4 h-4" />
                                Adicionar subcategoria
                              </button>
                              <button
                                onClick={() => startEditCategory(root)}
                                className="p-1.5 hover:bg-secondary rounded transition-colors flex-shrink-0"
                                style={{ borderRadius: "var(--radius-button)" }}
                                title="Editar"
                              >
                                <Edit20Regular className="w-5 h-5 text-foreground" />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(root)}
                                disabled={categoryBusy}
                                className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0 disabled:opacity-50"
                                style={{ borderRadius: "var(--radius-button)" }}
                                title="Eliminar"
                              >
                                <Delete20Regular className="w-5 h-5 text-destructive" />
                              </button>
                            </>
                          )}
                        </div>

                        {/* Subcategorias */}
                        {(children.length > 0 || subParentId === root.id) && (
                          <div className="border-t border-border pl-10 pr-3 py-2 space-y-2 bg-muted">
                            {children.map((child) => (
                              <div key={child.id} className="flex items-center gap-3 flex-wrap">
                                {editingCategoryId === child.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={editCategoryName}
                                      onChange={(e) => setEditCategoryName(e.target.value)}
                                      className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground"
                                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "10rem" }}
                                      placeholder="Nome"
                                    />
                                    <input
                                      type="text"
                                      value={editCategoryCode}
                                      onChange={(e) => setEditCategoryCode(e.target.value)}
                                      className="border border-border rounded px-2 py-1 bg-background text-foreground"
                                      style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", width: "9rem" }}
                                      placeholder="codigo"
                                    />
                                    <CostTypeSelect
                                      value={editCategoryCostType}
                                      onChange={setEditCategoryCostType}
                                    />
                                    <button
                                      onClick={() => handleSaveCategoryEdit(child)}
                                      disabled={categoryBusy}
                                      className="p-1.5 hover:bg-accent rounded transition-colors flex-shrink-0 disabled:opacity-50"
                                      style={{ borderRadius: "var(--radius-button)" }}
                                      title="Guardar"
                                    >
                                      <Checkmark20Regular className="w-5 h-5 text-green-700" />
                                    </button>
                                    <button
                                      onClick={() => setEditingCategoryId(null)}
                                      className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                                      style={{ borderRadius: "var(--radius-button)" }}
                                      title="Cancelar"
                                    >
                                      <Dismiss20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-1 text-foreground" style={{ minWidth: "8rem" }}>
                                      <span style={{ fontSize: "var(--text-sm)" }}>{child.name}</span>
                                      <span className="block text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                                        {child.code}
                                      </span>
                                    </span>
                                    {costTypeBadge(child.isStructural)}
                                    <button
                                      onClick={() => startEditCategory(child)}
                                      className="p-1.5 hover:bg-secondary rounded transition-colors flex-shrink-0"
                                      style={{ borderRadius: "var(--radius-button)" }}
                                      title="Editar"
                                    >
                                      <Edit20Regular className="w-5 h-5 text-foreground" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCategory(child)}
                                      disabled={categoryBusy}
                                      className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0 disabled:opacity-50"
                                      style={{ borderRadius: "var(--radius-button)" }}
                                      title="Eliminar"
                                    >
                                      <Delete20Regular className="w-5 h-5 text-destructive" />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}

                            {/* Form de criação de subcategoria */}
                            {subParentId === root.id && (
                              <div className="flex items-center gap-3 flex-wrap pt-1">
                                <input
                                  type="text"
                                  value={newSubName}
                                  onChange={(e) => {
                                    setNewSubName(e.target.value);
                                    if (!newSubCodeTouched) setNewSubCode(slugifyCategoryName(e.target.value));
                                  }}
                                  placeholder="Nome da subcategoria"
                                  className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground"
                                  style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "10rem" }}
                                />
                                <input
                                  type="text"
                                  value={newSubCodeTouched ? newSubCode : slugifyCategoryName(newSubName)}
                                  onChange={(e) => {
                                    setNewSubCodeTouched(true);
                                    setNewSubCode(e.target.value);
                                  }}
                                  placeholder="codigo"
                                  className="border border-border rounded px-2 py-1 bg-background text-foreground"
                                  style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", width: "9rem" }}
                                />
                                <CostTypeSelect value={newSubCostType} onChange={setNewSubCostType} />
                                <button
                                  onClick={() => handleCreateSubcategory(root.id)}
                                  disabled={categoryBusy || !newSubName.trim()}
                                  className="flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                  style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                                >
                                  <Checkmark20Regular className="w-4 h-4" />
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setSubParentId(null)}
                                  className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                                  style={{ borderRadius: "var(--radius-button)" }}
                                  title="Cancelar"
                                >
                                  <Dismiss20Regular className="w-5 h-5 text-destructive" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "categorias-despesa" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                {expenseRulesError && (
                  <p className="text-destructive text-sm">{expenseRulesError}</p>
                )}
                {expenseRulesSuccess && (
                  <p className="text-sm" style={{ color: "var(--color-success, #16a34a)" }}>{expenseRulesSuccess}</p>
                )}
                {expenseRulesLoading ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : (
                  <>
                    <p className="text-secondary-text text-sm">
                      Define a categoria das faturas de despesa que entram por email ou upload. Decide a primeira regra que casa — mas se já tiveres corrigido a categoria de uma fatura desse fornecedor, essa correção ganha às regras. A categoria escolhida manualmente prevalece sempre. "Palavra-chave" procura na designação e na entidade; "Fornecedor" compara com o nome da entidade.
                    </p>
                    <p className="text-secondary-text text-sm">
                      Nas categorias de estrutura (as marcadas com "estrutura", como Estaleiro ou Viaturas) a despesa nunca é imputada automaticamente a uma obra, mesmo que a leitura do documento proponha uma.
                    </p>

                    <div className="space-y-2">
                      {expenseRulesDraft.map((rule, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 border border-border rounded flex-wrap"
                          style={{ borderRadius: "var(--radius-card)" }}
                        >
                          <select
                            value={rule.matchType}
                            onChange={(e) => handleExpenseRuleChange(index, "matchType", e.target.value)}
                            className="border border-border rounded px-2 py-1 bg-background text-foreground flex-shrink-0"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                          >
                            <option value="keyword">Palavra-chave</option>
                            <option value="supplier">Fornecedor</option>
                          </select>
                          <input
                            type="text"
                            value={rule.matchValue}
                            onChange={(e) => handleExpenseRuleChange(index, "matchValue", e.target.value)}
                            className="flex-1 border border-border rounded px-2 py-1 bg-background text-foreground"
                            style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)", minWidth: "12rem" }}
                            placeholder="palavra a procurar ou nome do fornecedor"
                          />
                          {/* [2.4.1] Opções derivadas da árvore (subcategorias indentadas com "— ")
                              para que categorias novas apareçam de imediato. */}
                          <SearchableSelect<string>
                            ariaLabel="Categoria de despesa"
                            value={rule.categoryCode}
                            onChange={(next) =>
                              handleExpenseRuleChange(index, "categoryCode", next ?? "")
                            }
                            options={categorySelectOptions}
                            allowClear={false}
                            searchPlaceholder="Procurar categoria…"
                            className="flex-shrink-0"
                            style={
                              {
                                minWidth: "12rem",
                                "--text-base": "var(--text-sm)",
                              } as CSSProperties
                            }
                          />
                          <button
                            onClick={() => handleRemoveExpenseRule(index)}
                            className="p-1.5 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Remover"
                          >
                            <Delete20Regular className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {expenseRulesDraft.length === 0 && (
                      <div
                        className="p-8 text-center border border-border rounded"
                        style={{ borderRadius: "var(--radius-card)" }}
                      >
                        <p className="text-secondary-text text-sm">
                          Nenhuma regra configurada. Adiciona uma para começar.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between" style={{ paddingTop: "0.5rem" }}>
                      <button
                        onClick={handleAddExpenseRule}
                        className="flex items-center gap-2 px-3 py-2 border border-border rounded hover:bg-accent/50 transition-colors text-foreground"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        <Add20Regular className="w-4 h-4" />
                        Adicionar regra
                      </button>
                      <button
                        onClick={handleSaveExpenseRules}
                        disabled={expenseRulesSaving}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                        style={{ fontSize: "var(--text-sm)", borderRadius: "var(--radius-button)" }}
                      >
                        {expenseRulesSaving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "anomalias" && (
              <div className="space-y-4 overflow-y-auto p-4 md:p-6">
                {anomalyError && (
                  <p className="text-destructive text-sm">{anomalyError}</p>
                )}
                {anomalySuccess && (
                  <p className="text-sm" style={{ color: "var(--color-success, #16a34a)" }}>{anomalySuccess}</p>
                )}
                {anomalyLoading || !anomalyDraft ? (
                  <p className="text-secondary-text text-sm">A carregar...</p>
                ) : (
                  <>
                    <p className="text-secondary-text text-sm">
                      A deteção de anomalias assinala (de forma informativa, não bloqueante) faturas com características invulgares. Ative os verificadores que pretende usar.
                    </p>

                    <div className="space-y-2">
                      {([
                        { field: "nifCheckEnabled" as const, label: "NIF inválido ou em falta", help: "Assinala faturas sem NIF ou com NIF inválido." },
                        { field: "newSupplierEnabled" as const, label: "Fornecedor novo", help: "Assinala faturas de fornecedores ainda sem histórico." },
                        { field: "valueAboveUsualEnabled" as const, label: "Valor acima do habitual", help: "Assinala valores muito acima da média do fornecedor." },
                        { field: "weekendDateEnabled" as const, label: "Data ao fim de semana", help: "Assinala faturas datadas de sábado ou domingo." },
                        { field: "atypicalCategoryEnabled" as const, label: "Categoria atípica", help: "Assinala categorias de despesa pouco comuns para o fornecedor." },
                      ]).map((toggle) => (
                        <label
                          key={toggle.field}
                          className="flex items-center gap-3 p-3 border border-border rounded cursor-pointer"
                          style={{ borderRadius: "var(--radius-card)" }}
                        >
                          <input
                            type="checkbox"
                            checked={anomalyDraft[toggle.field]}
                            onChange={(e) => handleAnomalyToggle(toggle.field, e.target.checked)}
                            className="h-4 w-4 flex-shrink-0"
                            style={{ accentColor: "var(--primary)" }}
                          />
                          <span className="flex-1">
                            <span className="block text-foreground" style={{ fontSize: "var(--text-base)" }}>{toggle.label}</span>
                            <span className="block text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>{toggle.help}</span>
                          </span>
                        </label>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label
                          className="block text-foreground mb-2"
                          style={{ fontSize: "var(--text-caption)", fontWeight: "var(--font-weight-semi-bold)" }}
                        >
                          Multiplicador "valor acima do habitual"
                        </label>
                        <input
                          type="number"
                          value={anomalyMultiplierInput}
                          onChange={(e) => setAnomalyMultiplierInput(e.target.value)}
                          min={0}
                          step={0.1}
                          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base)", maxWidth: 200 }}
                        />
                        <p className="mt-1 text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                          Ex.: 3 = assinala valores 3x acima da média do fornecedor.
                        </p>
                      </div>
                      <div>
                        <label
                          className="block text-foreground mb-2"
                          style={{ fontSize: "var(--text-caption)", fontWeight: "var(--font-weight-semi-bold)" }}
                        >
                          Histórico mínimo (nº de faturas)
                        </label>
                        <input
                          type="number"
                          value={anomalyMinHistoryInput}
                          onChange={(e) => setAnomalyMinHistoryInput(e.target.value)}
                          min={1}
                          step={1}
                          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base)", maxWidth: 200 }}
                        />
                        <p className="mt-1 text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                          Nº mínimo de faturas do fornecedor antes de comparar valores.
                        </p>
                      </div>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={handleSaveAnomalySettings}
                        disabled={anomalySaving}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)" }}
                      >
                        {anomalySaving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "financeiro" && (
              <div className="p-4 md:p-6">
                <FinancialSettingsTab />
              </div>
            )}

            {activeTab === "bancos" && (
              <div className="p-4 md:p-6">
                <BankSyncSettingsTab />
              </div>
            )}

            {activeTab === "empresas" && (
              <div className="p-4 md:p-6">
                <CompaniesSettingsTab />
              </div>
            )}

            {activeTab === "estados-pagamento" && (
              <div className="p-4 md:p-6">
                <PaymentStatusSettingsTab />
              </div>
            )}

            {activeTab === "custos-indiretos" && (
              <div className="p-4 md:p-6">
                <IndirectCostSettingsTab />
              </div>
            )}

            {activeTab === "credit-control" && (
              <div className="p-4 md:p-6">
                <CreditControlSettingsTab />
              </div>
            )}

            {activeTab === "treasury" && (
              <div className="p-4 md:p-6">
                <TreasurySettingsTab />
              </div>
            )}

            {activeTab === "ia" && (
              <div className="p-4 md:p-6">
                <AiSettingsTab />
              </div>
            )}
          </div>

          {/* Mobile Bottom Navigation — fita horizontal com scroll. (Era grid-cols-6, classe que
              não existe no index.css pré-compilado: a grelha colapsava numa pilha vertical que
              ocupava o ecrã inteiro.) */}
          <div className="md:hidden border-t border-border bg-muted p-2 relative" style={{ flexShrink: 0 }}>
            {/* Sombra no bordo direito: diz ao toque que há mais separadores a deslizar. */}
            <div
              aria-hidden="true"
              className="absolute right-0 bottom-0"
              style={{
                top: 0,
                width: "24px",
                pointerEvents: "none",
                background: "linear-gradient(to left, var(--muted), transparent)",
              }}
            />
            <div className="flex gap-1 overflow-x-auto">
              {SETTINGS_NAV_SECTIONS.flatMap((section) => section.items).map(
                (item) => (
                  <button
                    key={item.tab}
                    onClick={() => setActiveTab(item.tab)}
                    className={`flex flex-col items-center gap-1 px-2 py-2 rounded transition-colors whitespace-nowrap ${
                      activeTab === item.tab
                        ? "bg-accent text-accent-foreground"
                        : "text-foreground"
                    }`}
                    style={{ borderRadius: "var(--radius-button)", flexShrink: 0, minWidth: "56px" }}
                  >
                    <item.icon className="w-5 h-5" />
                    <span style={{ fontSize: "10px" }}>{item.shortLabel}</span>
                  </button>
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
